import {memo, useEffect, useMemo, useState} from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@common-web/components/ui/sheet';
import {ScrollArea} from '@common-web/components/ui/scroll-area';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Label} from '@common-web/components/ui/label';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {ExtractedFile, ExtractedColumn} from '@client/pages/payroll/types';
import {Check, X, Eye, ListPlus} from 'lucide-react';
import {PayrollFileColumnTransformationColumn} from '@api/lib/payrollProcessing/types';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';
import clsx from 'clsx';

export const DeductionCombineModal = memo(
  ({
    open,
    onClose,
    targetFieldLabel,
    extracted,
    initialColumns,
    onConfirm,
  }: {
    open: boolean;
    onClose: () => void;
    targetFieldLabel: string;
    extracted: ExtractedFile[];
    initialColumns?: PayrollFileColumnTransformationColumn[];
    onConfirm: (columns: PayrollFileColumnTransformationColumn[]) => void;
  }) => {
    const [selected, setSelected] = useState<Record<string, boolean>>({});

    useEffect(() => {
      const init: typeof selected = {};
      (initialColumns || []).forEach((c) => (init[`${c.fileId}:${c.index}`] = true));
      setSelected(init);
    }, [open]);

    const allColumns = useMemo(() => {
      const out: {fileId: string; fileName: string; columns: ExtractedColumn[]}[] = [];
      for (const f of extracted) {
        const allFileColumns: ExtractedColumn[] = [...f.columns];
        if (f.pivotColumns) {
          allFileColumns.push(
            ...f.pivotColumns.map((pc) => ({
              name: pc.name,
              index: pc.index,
              samples: pc.samples,
              pivotKeyValue: pc.pivotKeyValue,
            })),
          );
        }
        out.push({fileId: f.file.fileId, fileName: f.file.fileName, columns: allFileColumns});
      }
      return out;
    }, [extracted]);

    const selectedList = useMemo(() => {
      const list: PayrollFileColumnTransformationColumn[] = [];
      for (const f of allColumns) {
        for (const c of f.columns) {
          if (selected[`${f.fileId}:${c.index}`]) {
            list.push({
              name: c.name,
              index: c.index,
              fileId: f.fileId,
              pivotKeyValue: c.pivotKeyValue,
            });
          }
        }
      }
      return list;
    }, [selected, allColumns]);

    const previewData = useMemo(() => {
      if (selectedList.length === 0) {
        return {before: 'No columns selected', after: 'No preview available'};
      }

      const columnInfo: {name: string; value: string | null}[] = [];
      for (const selectedColumn of selectedList) {
        const file = allColumns.find((f) => f.fileId === selectedColumn.fileId);
        const extractedColumn = file?.columns.find((c) => c.index === selectedColumn.index);
        columnInfo.push({
          name: extractedColumn?.name || selectedColumn.name,
          value: extractedColumn?.samples?.[0] ?? null,
        });
      }

      const beforeParts = columnInfo.map((c) => (c.value ? c.value : `[${c.name}]`));
      const before = beforeParts.join(' + ');

      let sum = 0;
      let hasAnyValue = false;
      for (const column of columnInfo) {
        if (column.value) {
          const num = parseFloat(String(column.value).replace(/[^0-9.-]/g, ''));
          if (!isNaN(num) && isFinite(num)) {
            sum += num;
            hasAnyValue = true;
          }
        }
      }

      const columnsWithoutSamples = columnInfo.filter((c) => !c.value).length;
      let after = hasAnyValue ? sum.toString() : 'Cannot calculate';
      if (columnsWithoutSamples > 0 && hasAnyValue) {
        after += ` (+ ${columnsWithoutSamples} more)`;
      }

      return {before, after};
    }, [selectedList, allColumns]);

    const toggle = (fileId: string, index: number) => {
      const key = `${fileId}:${index}`;
      setSelected((prev) => ({...prev, [key]: !prev[key]}));
    };

    const clearAll = () => {
      setSelected({});
    };

    const onSave = () => {
      onConfirm(selectedList);
      onClose();
    };

    return (
      <Sheet
        open={open}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent className='w-full sm:max-w-2xl lg:max-w-4xl overflow-hidden flex flex-col'>
          <SheetHeader className='flex-shrink-0'>
            <SheetTitle className='flex items-center gap-2 text-internal-brand-navy'>
              <ListPlus className='h-5 w-5 text-internal-brand-blue' />
              Combine deduction columns for <span className='font-bold'>{targetFieldLabel}</span>
            </SheetTitle>
            <SheetDescription className='text-sm text-internal-brand-gray-dark'>
              Select all columns that represent deductions. We'll add them together to get the total
              per row.
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className='flex-1 mt-6'>
            <div className='flex-1 flex flex-col gap-6 min-h-0'>
              <div className='flex-1 flex flex-col gap-3 min-h-0'>
                <div className='flex items-center justify-between px-3'>
                  <Label className='text-sm font-semibold text-internal-brand-navy'>
                    Available Columns
                  </Label>
                  <Badge variant='outline' className='text-xs'>
                    {allColumns.reduce((acc, f) => acc + f.columns.length, 0)} total
                  </Badge>
                </div>
                <div className='flex-1 border border-gray-200 rounded-lg bg-white'>
                  <div className='space-y-3 p-4'>
                    {allColumns.length === 0 ? (
                      <div className='text-sm text-internal-brand-gray italic py-8 text-center'>
                        No extracted columns available.
                      </div>
                    ) : (
                      allColumns.map((columnOption) => (
                        <Card
                          key={columnOption.fileId}
                          className='border-gray-200 shadow-none hover:shadow-sm transition-shadow bg-white overflow-hidden'
                        >
                          <div className='bg-internal-brand-blue/10 border-b border-internal-brand-blue/20 px-4 py-3 flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                              <FileIdBadge fileId={columnOption.fileId} />
                              <span className='text-sm font-medium text-internal-brand-navy'>
                                {columnOption.fileName}
                              </span>
                            </div>
                            <div className='text-xs text-gray-500 font-medium'>
                              {columnOption.columns.length} column
                              {columnOption.columns.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <div>
                            <div
                              className={
                                'p-3 items-center grid grid-cols-12 border-b border-gray-200 bg-gray-50'
                              }
                            >
                              <div className={'col-span-1'}></div>
                              <div className='col-span-6 text-xs font-semibold text-internal-brand-navy'>
                                Column
                              </div>
                              <div className='col-span-5 text-xs font-semibold text-internal-brand-navy'>
                                Example Value
                              </div>
                            </div>
                            <div>
                              {columnOption.columns.map((c) => {
                                const key = `${columnOption.fileId}:${c.index}`;
                                const isChecked = !!selected[key];
                                return (
                                  <div
                                    key={key}
                                    className={clsx(
                                      'p-3 items-center grid grid-cols-12 border-gray-100 hover:bg-internal-brand-blue/5 transition-colors even:bg-gray-50 even:border-y even:border-gray-200',
                                      isChecked ? 'bg-internal-brand-blue/5' : '',
                                    )}
                                  >
                                    <div className={'col-span-1'}>
                                      <Checkbox
                                        checked={isChecked}
                                        onCheckedChange={() => toggle(columnOption.fileId, c.index)}
                                        className='border-internal-brand-blue data-[state=checked]:bg-internal-brand-blue data-[state=checked]:border-internal-brand-blue'
                                      />
                                    </div>
                                    <div className='col-span-6 font-medium text-sm text-internal-brand-navy truncate'>
                                      {c.name}
                                    </div>
                                    <div className='col-span-5 text-sm text-internal-brand-gray font-mono truncate'>
                                      {c.samples?.[0] || '—'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <Card className='flex-shrink-0 border-emerald/20 bg-emerald/5'>
                <CardHeader className='py-4 bg-gradient-to-r from-emerald/10 to-emerald/20 border-b border-emerald/20'>
                  <CardTitle className='text-sm font-semibold text-internal-brand-navy flex items-center gap-2'>
                    <Eye className='h-4 w-4 text-emerald' />
                    Live Preview
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-4'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label className='text-xs font-semibold text-internal-brand-navy'>
                        Values to Combine
                      </Label>
                      <div className='p-3 bg-white border border-gray-200 text-gray-600 rounded-lg font-mono text-sm'>
                        {previewData.before || <span className='italic'>No columns selected</span>}
                      </div>
                    </div>
                    <div className='space-y-2'>
                      <Label className='text-xs font-semibold text-internal-brand-navy'>
                        Combined Total
                      </Label>
                      <div className='p-3 bg-white border border-gray-200 rounded-lg text-gray-600 font-mono text-sm'>
                        {previewData.after || <span className='italic'>No preview available</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
          <div className='flex-shrink-0 border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-2'>
            <div className='text-sm font-medium text-internal-brand-navy'>
              <span className='font-medium'>{selectedList.length}</span>
              <span className='ml-1'>column{selectedList.length !== 1 ? 's' : ''} selected</span>
            </div>
            <div className='flex items-center gap-2'>
              <Button
                variant='ghost'
                onClick={clearAll}
                className='text-red-600 hover:bg-red-50 hover:text-red-700 font-medium'
              >
                <X className='h-4 w-4 mr-1' /> Clear
              </Button>
              <Button
                onClick={onSave}
                disabled={selectedList.length === 0}
                className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'
              >
                <Check className='h-4 w-4 mr-1' /> Save
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  },
);
